import React, { createContext, useContext, useState, useEffect } from 'react';

export type ThemeMode = 'red' | 'dark' | 'blue' | 'purple';

interface ThemeColors {
  sidebarBg: string;
  sidebarText: string;
  navbarBg: string;
  navbarText: string;
  mainBg: string;
  accentColor: string;
  button3D: string;
  activeItem: string;
  // New Properties for Dynamic Cards & Tables
  cardBg: string;
  cardText: string;
  borderColor: string;
  inputBg: string;
  tableHeaderBg: string;
}

const themes: Record<ThemeMode, ThemeColors> = {
  red: {
    sidebarBg: 'bg-gradient-to-b from-red-900 to-red-800',
    sidebarText: 'text-red-100',
    navbarBg: 'bg-white',
    navbarText: 'text-gray-800',
    mainBg: 'bg-red-50',
    accentColor: 'text-red-600',
    button3D: 'bg-red-600 hover:bg-red-500 shadow-red-900',
    activeItem: 'bg-red-700/50 border-l-4 border-white',
    cardBg: 'bg-white',
    cardText: 'text-gray-800',
    borderColor: 'border-red-100',
    inputBg: 'bg-white',
    tableHeaderBg: 'bg-red-100 text-red-800'
  },
  dark: {
    sidebarBg: 'bg-gray-950',
    sidebarText: 'text-gray-400',
    navbarBg: 'bg-gray-900',
    navbarText: 'text-white',
    mainBg: 'bg-black', // Deep black background
    accentColor: 'text-red-500',
    button3D: 'bg-gray-800 hover:bg-gray-700 shadow-black',
    activeItem: 'bg-gray-800 border-l-4 border-red-500',
    cardBg: 'bg-gray-900', // Dark Cards
    cardText: 'text-gray-200', // Light Text
    borderColor: 'border-gray-800',
    inputBg: 'bg-gray-800 text-white',
    tableHeaderBg: 'bg-gray-800 text-gray-300'
  },
  blue: {
    sidebarBg: 'bg-gradient-to-b from-blue-900 to-indigo-900',
    sidebarText: 'text-blue-100',
    navbarBg: 'bg-white',
    navbarText: 'text-gray-800',
    mainBg: 'bg-slate-100',
    accentColor: 'text-blue-600',
    button3D: 'bg-blue-600 hover:bg-blue-500 shadow-blue-900',
    activeItem: 'bg-blue-800/50 border-l-4 border-cyan-400',
    cardBg: 'bg-white',
    cardText: 'text-gray-800',
    borderColor: 'border-blue-100',
    inputBg: 'bg-white',
    tableHeaderBg: 'bg-blue-50 text-blue-800'
  },
  purple: {
    sidebarBg: 'bg-gradient-to-b from-purple-900 to-fuchsia-900',
    sidebarText: 'text-purple-100',
    navbarBg: 'bg-white',
    navbarText: 'text-gray-800',
    mainBg: 'bg-fuchsia-50',
    accentColor: 'text-purple-600',
    button3D: 'bg-purple-600 hover:bg-purple-500 shadow-purple-900',
    activeItem: 'bg-purple-800/50 border-l-4 border-yellow-400',
    cardBg: 'bg-white',
    cardText: 'text-gray-800',
    borderColor: 'border-purple-100',
    inputBg: 'bg-white',
    tableHeaderBg: 'bg-purple-50 text-purple-800'
  }
};

interface AdminThemeContextType {
  theme: ThemeMode;
  colors: ThemeColors;
  setTheme: (theme: ThemeMode) => void;
}

const AdminThemeContext = createContext<AdminThemeContextType | undefined>(undefined);

export const AdminThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeMode>('red');

  useEffect(() => {
    const saved = localStorage.getItem('admin-theme') as ThemeMode;
    if (saved && themes[saved]) setTheme(saved);
  }, []);

  const changeTheme = (newTheme: ThemeMode) => {
    setTheme(newTheme);
    localStorage.setItem('admin-theme', newTheme);
  };

  return (
    <AdminThemeContext.Provider value={{ theme, colors: themes[theme], setTheme: changeTheme }}>
      {children}
    </AdminThemeContext.Provider>
  );
};

export const useAdminTheme = () => {
  const context = useContext(AdminThemeContext);
  if (!context) throw new Error("useAdminTheme must be used within AdminThemeProvider");
  return context;
};